import { nodeSourceFactory } from "Areas/Documents/graph";
import { OPTION_TYPE } from "Common/dictionaries/optionType";
import { TableLayoutTabItemId } from "devexpress-richedit";
import { TableWidthUnitType } from "devexpress-richedit/lib/core/model/tables/secondary-structures/table-units";
import filter from "lodash/filter";
import find from "lodash/find";
import get from "lodash/get";
import includes from "lodash/includes";
import isArray from "lodash/isArray";
import join from "lodash/join";
import map from "lodash/map";
import sortBy from "lodash/sortBy";
import toString from "lodash/toString";
import uniqBy from "lodash/uniqBy";
import locale from "./addToReportDocumentObjectNodes.locale";
import { defaultCharacterProperties, pageSubHeaderCharacterProperties } from "./characterProperties";
import getNodeImageBase64, { nodeLabelDefaultStyle } from "./getNodeImageBase64";
const nodeNameCharacterProperties = {
    ...defaultCharacterProperties,
    size: 14,
    bold: true
};
const documentObjectNameCharacterProperties = {
    ...defaultCharacterProperties,
    foreColor: "#666"
};
const attrLabelCharacterProperties = {
    ...defaultCharacterProperties,
    foreColor: "#969696"
};
const SIZES = {
    LEFT_INDENT: 200,
    RIGHT_INDENT: 200,
    SPACING_BEFORE: 90,
    SPACING_AFTER: 120,
    RIGHT_CELL_PREFERRED_WIDTH_INCH: 8000
};
export default async (richRef, documentObjects, title, nodeSources, progressRef, abortControllerRef, processResolve) => {
    var _a;
    if (!richRef.current) {
        processResolve(false);
        return;
    }
    let hasTransaction = false;
    const nodeSourcesEnriched = sortBy(map(nodeSources, node => nodeSourceFactory(node, documentObjects)), "NodeId");
    (_a = progressRef.current) === null || _a === void 0 ? void 0 : _a.setMessage(locale.PREPARING_IMAGES, async () => {
        var _a, _b, _c, _d;
        if (!richRef.current) {
            processResolve(false);
            return;
        }
        const base64DefaultImagesMap = {};
        const base64ImagesMap = {};
        const nodeSourcesWithDefaultImages = uniqBy(filter(nodeSourcesEnriched, nodeSource => Boolean(!nodeSource.NodeDefaultImage && nodeSource.NodeImage)), "NodeImage");
        await Promise.all(map(nodeSourcesWithDefaultImages, async (nodeSource) => {
            var _a;
            if (!nodeSource.NodeImage)
                return;
            if ((_a = abortControllerRef.current) === null || _a === void 0 ? void 0 : _a.signal.aborted)
                return;
            const base64 = await getNodeImageBase64(nodeSource, abortControllerRef.current);
            base64DefaultImagesMap[nodeSource.NodeImage] = base64;
        }));
        if ((_a = abortControllerRef.current) === null || _a === void 0 ? void 0 : _a.signal.aborted) {
            processResolve(false);
            return;
        }
        await Promise.all(map(nodeSourcesEnriched, async (nodeSource) => {
            var _a;
            if (!nodeSource.NodeImage)
                return;
            if ((_a = abortControllerRef.current) === null || _a === void 0 ? void 0 : _a.signal.aborted)
                return;
            const nodeImage = !nodeSource.NodeDefaultImage && nodeSource.NodeImage
                ? base64DefaultImagesMap[nodeSource.NodeImage]
                : undefined;
            if (nodeImage) {
                base64ImagesMap[nodeSource.NodeId] = nodeImage;
            }
            else {
                base64ImagesMap[nodeSource.NodeId] = await getNodeImageBase64(nodeSource, abortControllerRef.current);
            }
        }));
        if ((_b = abortControllerRef.current) === null || _b === void 0 ? void 0 : _b.signal.aborted) {
            processResolve(false);
            return;
        }
        const initialPosition = richRef.current.selection.active;
        const endUpdate = (error, needProcessResolve) => {
            var _a;
            if (!hasTransaction)
                return;
            (_a = progressRef.current) === null || _a === void 0 ? void 0 : _a.setMessage(locale.PROCESS_COMPLETION, async () => {
                if (!richRef.current)
                    return;
                needProcessResolve &&
                    richRef.current.selection.setSelection({ start: initialPosition, length: 0 });
                richRef.current.history.endTransaction();
                richRef.current.endUpdate();
                needProcessResolve && processResolve(!error);
                hasTransaction = false;
            });
        };
        richRef.current.beginUpdate();
        richRef.current.history.beginTransaction();
        hasTransaction = true;
        const subDocument = richRef.current.selection.activeSubDocument;
        let position = initialPosition;
        subDocument.insertParagraph(position);
        subDocument.setCharacterProperties({ start: position, length: 1 }, defaultCharacterProperties);
        subDocument.insertText(position, title);
        subDocument.setCharacterProperties({ start: position, length: title.length }, pageSubHeaderCharacterProperties);
        position += title.length;
        subDocument.insertParagraph(position);
        subDocument.insertParagraph(position);
        subDocument.setCharacterProperties({ start: position, length: 2 }, defaultCharacterProperties);
        position += 2;
        if ((_c = abortControllerRef.current) === null || _c === void 0 ? void 0 : _c.signal.aborted) {
            endUpdate(true, true);
            return;
        }
        endUpdate(false, false);
        (_d = progressRef.current) === null || _d === void 0 ? void 0 : _d.setMessage(locale.buildCreatingTable(2, nodeSourcesEnriched.length), async () => {
            var _a, _b, _c;
            if (!richRef.current) {
                processResolve(false);
                return;
            }
            richRef.current.beginUpdate();
            richRef.current.history.beginTransaction();
            hasTransaction = true;
            const tableApi = subDocument.tables.create(position, 2, nodeSourcesEnriched.length);
            richRef.current.selection.setSelection(tableApi.interval);
            (_a = richRef.current) === null || _a === void 0 ? void 0 : _a.executeCommand(TableLayoutTabItemId.SetAutoFitWindow);
            const table = get(tableApi, "_table");
            if (!table || ((_b = abortControllerRef.current) === null || _b === void 0 ? void 0 : _b.signal.aborted)) {
                endUpdate(true, true);
                return;
            }
            const nodeLabelWidth = richRef.current.unitConverter.pixelsToTwips(nodeLabelDefaultStyle.width);
            const nodeLabelHeight = richRef.current.unitConverter.pixelsToTwips(nodeLabelDefaultStyle.height);
            table.rows.every(row => {
                var _a;
                if ((_a = abortControllerRef.current) === null || _a === void 0 ? void 0 : _a.signal.aborted)
                    return false;
                if (row.cells.length !== 2)
                    return false;
                let cell = row.cells.at(0);
                if (!cell)
                    return false;
                cell.preferredWidth.type = TableWidthUnitType.ModelUnits;
                cell.preferredWidth.value = nodeLabelWidth;
                cell = row.cells.at(1);
                if (!cell)
                    return false;
                cell.preferredWidth.type = TableWidthUnitType.ModelUnits;
                cell.preferredWidth.value = SIZES.RIGHT_CELL_PREFERRED_WIDTH_INCH;
                return true;
            });
            endUpdate(false, false);
            richRef.current.beginUpdate();
            richRef.current.history.beginTransaction();
            hasTransaction = true;
            let textApi;
            let rowsInsertedCount = 0;
            await Promise.all(map(nodeSourcesEnriched, async (item, ind) => new Promise(resolve => {
                setTimeout(() => {
                    var _a, _b, _c, _d, _e, _f, _g;
                    if (!richRef.current ||
                        richRef.current.isDisposed ||
                        ((_a = abortControllerRef.current) === null || _a === void 0 ? void 0 : _a.signal.aborted)) {
                        resolve(rowsInsertedCount);
                        processResolve(false);
                        return;
                    }
                    const row = table === null || table === void 0 ? void 0 : table.rows.at(ind);
                    if (!row || row.cells.length !== 2) {
                        resolve(rowsInsertedCount);
                        return;
                    }
                    let lineInterval = null;
                    let firstLineInterval = null;
                    let lastLineInterval = null;
                    let cell = row.cells.at(0);
                    if (!cell) {
                        resolve(rowsInsertedCount);
                        return;
                    }
                    const nodeImage = base64ImagesMap[item.NodeId];
                    nodeImage &&
                        subDocument.insertPicture(cell.interval.start, nodeImage, {
                            width: nodeLabelWidth,
                            height: nodeLabelHeight
                        });
                    if ((_b = abortControllerRef.current) === null || _b === void 0 ? void 0 : _b.signal.aborted) {
                        resolve(rowsInsertedCount);
                        processResolve(false);
                        return;
                    }
                    cell = row.cells.at(1);
                    if (!cell) {
                        resolve(rowsInsertedCount);
                        return;
                    }
                    position = cell.interval.start;
                    let hasAboveText = false;
                    if (item.NodeName) {
                        hasAboveText = true;
                        textApi = subDocument.insertText(position, item.NodeName);
                        lineInterval = { start: textApi.start, length: textApi.length };
                        subDocument.setCharacterProperties(lineInterval, nodeNameCharacterProperties);
                        firstLineInterval = lineInterval;
                        lastLineInterval = lineInterval;
                        position += textApi.length;
                    }
                    if ((_c = item.DocumentObject) === null || _c === void 0 ? void 0 : _c.Name) {
                        if (hasAboveText) {
                            subDocument.insertParagraph(position);
                            position += 1;
                        }
                        hasAboveText = true;
                        textApi = subDocument.insertText(position, (_d = item.DocumentObject) === null || _d === void 0 ? void 0 : _d.Name);
                        lineInterval = { start: textApi.start, length: textApi.length };
                        subDocument.setCharacterProperties({ start: textApi.start, length: textApi.length }, documentObjectNameCharacterProperties);
                        if (!firstLineInterval)
                            firstLineInterval = lineInterval;
                        lastLineInterval = lineInterval;
                        position += textApi.length;
                    }
                    if ((_e = abortControllerRef.current) === null || _e === void 0 ? void 0 : _e.signal.aborted) {
                        resolve(rowsInsertedCount);
                        processResolve(false);
                        return;
                    }
                    if (item.AttributeValues && item.DocumentObject) {
                        if (hasAboveText) {
                            subDocument.insertParagraph(position);
                            subDocument.setCharacterProperties({ start: position, length: 1 }, defaultCharacterProperties);
                            position += 1;
                        }
                        item.AttributeValues.every(val => {
                            var _a, _b, _c;
                            if ((_a = abortControllerRef.current) === null || _a === void 0 ? void 0 : _a.signal.aborted) {
                                processResolve(false);
                                return false;
                            }
                            const attr = find((_b = item.DocumentObject) === null || _b === void 0 ? void 0 : _b.Attributes, ["Key", val.Key]);
                            if (!attr ||
                                !attr.Type ||
                                includes((_c = item.DocumentObject) === null || _c === void 0 ? void 0 : _c.LabelAttributes, attr.Key))
                                return true;
                            if (attr.Type === OPTION_TYPE.BOOL ||
                                attr.Type === OPTION_TYPE.STRING ||
                                attr.Type === OPTION_TYPE.INT ||
                                attr.Type === OPTION_TYPE.TAGS) {
                                if (hasAboveText) {
                                    subDocument.insertParagraph(position);
                                    position += 1;
                                }
                                hasAboveText = true;
                                textApi = subDocument.insertText(position, `${attr.Name}: `);
                                lineInterval = { start: textApi.start, length: textApi.length };
                                subDocument.setCharacterProperties(lineInterval, attrLabelCharacterProperties);
                                if (!firstLineInterval)
                                    firstLineInterval = lineInterval;
                                lastLineInterval = lineInterval;
                                position += textApi.length;
                                textApi = subDocument.insertText(position, isArray(val.Value)
                                    ? join(map(val.Value, toString), ", ")
                                    : toString(val.Value));
                                lineInterval = { start: textApi.start, length: textApi.length };
                                subDocument.setCharacterProperties(lineInterval, defaultCharacterProperties);
                                if (!firstLineInterval)
                                    firstLineInterval = lineInterval;
                                lastLineInterval = lineInterval;
                                position += textApi.length;
                            }
                            return true;
                        });
                    }
                    if ((_f = abortControllerRef.current) === null || _f === void 0 ? void 0 : _f.signal.aborted) {
                        resolve(rowsInsertedCount);
                        processResolve(false);
                        return;
                    }
                    subDocument.setParagraphProperties(cell.interval, {
                        leftIndent: SIZES.LEFT_INDENT,
                        rightIndent: SIZES.RIGHT_INDENT
                    });
                    firstLineInterval &&
                        subDocument.setParagraphProperties(firstLineInterval, {
                            spacingBefore: SIZES.SPACING_BEFORE
                        });
                    lastLineInterval &&
                        subDocument.setParagraphProperties(lastLineInterval, {
                            spacingAfter: SIZES.SPACING_AFTER
                        });
                    rowsInsertedCount += 1;
                    (_g = progressRef.current) === null || _g === void 0 ? void 0 : _g.setMessage(locale.buildRowsCountCompleted(rowsInsertedCount, nodeSourcesEnriched.length));
                    resolve(rowsInsertedCount);
                });
            })));
            endUpdate(Boolean((_c = abortControllerRef.current) === null || _c === void 0 ? void 0 : _c.signal.aborted), true);
        });
    });
};
